/**
 * 熊本県の背景ツアー「熊本めぐり」コンテンツ。
 *
 * 軸は季節。冬から春の御輿来海岸から始めて、一年かけて冬の熊本城へ戻る。
 *
 * 熊本城について。
 *  平成28年と令和8年、二度の地震の被災地であり、復旧の途中にある。
 *  「復旧が終わった城」と書かない。城全体の完了は2052年度の計画。
 *  天守閣は2021年に復旧が終わり、いまも登れる。ただし今の天守は1960年の
 *  鉄筋鉄骨コンクリート再建で、加藤清正が建てた当時のものではない。
 *  宇土櫓は2025年に解体保存が完了しており、いま建っていない。
 *  絵にも工事の素屋根と特別見学通路を残し、復旧の途中であることを消さない。
 *
 * 阿蘇について。
 *  噴火警戒レベルは変わるので、数字を豆知識に書かない。
 *  火口周辺が規制されること、出かける前に確認することだけを書く。
 *  阿蘇大橋は平成28年熊本地震で崩落し、新阿蘇大橋が2021年3月に開通している。
 *  「阿蘇大橋を渡る」と書かない。
 *
 * 﨑津集落について。
 *  solemn を立てている。いまも使われているカトリック教会で、祭壇は絵踏が
 *  行われた場所に置かれている。天草市自身が信仰の場として静かにするよう求めている。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 「日本三名城」は熊本城公式が一度も使っていない。
 *  - 阿蘇を「世界一のカルデラ」と書かない。阿蘇市・環境省とも「世界最大級」。
 *  - 通潤橋の橋長は農水省75.6mと熊本県約78.0mで食い違う。条件つきで書く。
 *  - 通潤橋を「土木構造物として初の国宝」と書かない。文化庁・県の解説で確認できない。
 *  - 「日本三急流」（球磨川）は国土交通省が明記しているので使える数少ない例。
 *  - 菊池渓谷を「日本の滝百選」で飾らない。裏が取れるのは名水百選だけ。
 *  - 御輿来海岸の「渚百選」「日本の夕陽百選」は国の選定ではない。
 *  - 2026年の山鹿灯籠まつりは大幅に縮小されたが、年ごとに変わる情報なので豆知識にしない。
 */

import type { StageTour } from '../types';

export const kumamotoSceneryTour: StageTour = {
  scenes: [
    {
      id: 'okoshiki',
      name: '御輿来海岸',
      area: '宇土市・冬から春の日没前',
      image: '/assets/scenery/kumamoto/scenes/01_okoshiki.webp',
      hotspots: [
        {
          id: 'okoshiki-kyokusen',
          x: 0.46,
          y: 0.46,
          title: '風と波がつくる曲線',
          body: '有明海は潮の満ち引きの差が大きく、潮が引いた砂地に風と波がつくった曲線が現れます。干潟は時間帯で色を変え、夕陽と重なるとオレンジ、薄暮にはパープル、日中はシルバーに見えます。',
          source: 'https://kumamoto.guide/spots/detail/12235',
          mapQuery: '御輿来海岸',
        },
        {
          id: 'okoshiki-migoro',
          x: 0.72,
          y: 0.34,
          title: '見ごろは一月から四月',
          body: '夕陽に照らされた干潟が見事なのは1月から4月とされ、毎年多くのカメラマンを魅了しています。砂の曲線は潮が引いたときに現れるので、潮の時刻を確かめてから出かけましょう。',
          source: 'https://kumamoto.guide/spots/detail/12235',
          mapQuery: '御輿来海岸',
        },
      ],
    },
    {
      id: 'kusasenrigahama',
      name: '草千里ヶ浜と米塚',
      area: '阿蘇市・初夏の午前',
      image: '/assets/scenery/kumamoto/scenes/02_kusasenrigahama.webp',
      hotspots: [
        {
          id: 'kusasenri-meisho',
          x: 0.46,
          y: 0.44,
          title: '火山としては初の名勝',
          body: '米塚及び草千里ヶ浜は2013年3月27日に国の名勝及び天然記念物に指定されました。草千里ヶ浜はおよそ3万年前の火口の跡、米塚はおよそ3000年前にできた円すい形の火山です。',
          source: 'https://online.bunka.go.jp/heritages/detail/278740',
          mapQuery: '草千里ヶ浜',
        },
        {
          id: 'aso-sennen',
          x: 0.72,
          y: 0.32,
          title: '千年の草原',
          body: '阿蘇の草原は、地域の人たちが長い年月にわたって放牧・野焼き・採草を続けてきたために保たれてきました。環境省はこれを「千年の草原」と呼び、担い手の減少を課題に挙げています。',
          source: 'https://www.env.go.jp/park/aso/effort/nature_aso.html',
          mapQuery: '草千里ヶ浜',
        },
        {
          id: 'aso-kako',
          x: 0.22,
          y: 0.26,
          title: '火口には近づけません',
          body: '阿蘇山はいまも活動している火山です。噴火警戒レベルによって中岳火口の周辺が立入規制され、火口を見に行けない時期があります。出かける前に、阿蘇市と気象庁の最新の発表を必ず確かめてください。',
          source: 'https://www.data.jma.go.jp/vois/data/report/activity_info/503.html',
          mapQuery: '阿蘇山',
        },
      ],
    },
    {
      id: 'nabegataki',
      name: '鍋ヶ滝',
      area: '小国町・初夏の昼',
      image: '/assets/scenery/kumamoto/scenes/03_nabegataki.webp',
      hotspots: [
        {
          id: 'nabe-ura',
          x: 0.46,
          y: 0.4,
          title: '滝の裏側に回れる',
          body: '高さおよそ10メートル、幅およそ20メートル。カーテンのように流れ落ちる滝で、ときには滝の裏に入って、流れを間近に眺めることもできます。',
          source: 'https://kumamoto.guide/spots/detail/11869',
          mapQuery: '鍋ヶ滝',
        },
        {
          id: 'nabe-yoyaku',
          x: 0.74,
          y: 0.5,
          title: '入園は事前予約制',
          body: '鍋ヶ滝公園の入園はウェブからの事前予約制で、入園する時刻も指定されます。思い立って行っても入れないことがあるので、予約を取ってから出かけてください。',
          source: 'https://kumamoto.guide/spots/detail/11869',
          mapQuery: '鍋ヶ滝公園',
        },
      ],
    },
    {
      id: 'kikuchi-keikoku',
      name: '菊池渓谷',
      area: '菊池市・夏の朝',
      image: '/assets/scenery/kumamoto/scenes/04_kikuchi_keikoku.webp',
      hotspots: [
        {
          id: 'kikuchi-14do',
          x: 0.46,
          y: 0.42,
          title: '一年中14℃の水',
          body: '環境省の名水百選に「菊池水源」として選ばれています。原生林から湧き出る伏流水は一年を通じて水温14度。オオサンショウウオの生息も確認されています。',
          source: 'https://water-pub.env.go.jp/water-pub/mizu-site/meisui/data/index.asp?info=90',
          mapQuery: '菊池渓谷',
        },
        {
          id: 'kikuchi-kikan',
          x: 0.72,
          y: 0.5,
          title: '開いているのは春から秋',
          body: '係員がいるのは4月1日から11月30日までです。高校生以上は環境保全協力金がかかります。遊泳は禁止で、大雨警報のときは閉鎖されます。',
          source: 'https://kikuchikeikoku.com/',
          mapQuery: '菊池渓谷',
        },
      ],
    },
    {
      id: 'kumagawa-aoi',
      name: '球磨川と青井阿蘇神社',
      area: '人吉市・夏の昼過ぎ',
      image: '/assets/scenery/kumamoto/scenes/05_kumagawa_aoi.webp',
      hotspots: [
        {
          id: 'kumagawa-kyuryu',
          x: 0.4,
          y: 0.5,
          title: '日本三急流のひとつ',
          body: '国土交通省は球磨川を、最上川・富士川と並ぶ日本三急流のひとつとしています。球磨郡水上村の銚子笠に源を発し、115キロを流れて不知火海に注ぐ、熊本県最大の川です。',
          source:
            'https://www.mlit.go.jp/river/toukei_chousa/kasen/jiten/nihon_kawa/0911_kumagawa/0911_kumagawa_00.html',
          mapQuery: '球磨川',
        },
        {
          id: 'aoi-kokuho',
          x: 0.7,
          y: 0.3,
          title: '熊本県で初めての国宝',
          body: '青井阿蘇神社の本殿・廊・幣殿・拝殿・楼門の5棟は、2008年6月9日に国宝に指定されました。いまの社殿は1610年から1613年にかけて造られたものです。',
          source:
            'https://www.city.hitoyoshi.lg.jp/kosodate_kyoiku/kyoiku_bunka/rekishi_bunka/2421063',
          mapQuery: '青井阿蘇神社',
        },
        {
          id: 'kumagawa-fukko',
          x: 0.22,
          y: 0.34,
          title: '球磨川くだり',
          body: '人吉では、球磨川を木舟で下る「球磨川くだり」が楽しめます。船上からは人吉城跡や、四季折々で表情を変える山々を眺められます。冬場はコタツ舟も運航されます。',
          source: 'https://www.city.hitoyoshi.lg.jp/kanko/kankojyoho/21341',
          mapQuery: '球磨川くだり',
        },
      ],
    },
    {
      id: 'yachiyoza',
      name: '八千代座と山鹿灯籠',
      area: '山鹿市・夏の夜',
      image: '/assets/scenery/kumamoto/scenes/06_yachiyoza.webp',
      hotspots: [
        {
          id: 'yamaga-honenashi',
          x: 0.46,
          y: 0.44,
          title: '骨のない灯籠',
          body: '山鹿灯籠は、木や金具を一切使わず、和紙と少量の糊だけで作られます。灯籠を頭にのせて踊るよへほ節には、「骨もなけれど肉もなし」という歌詞があります。',
          source: 'https://yamaga-tanbou.jp/about/toromatsuri/',
          mapQuery: '山鹿灯籠',
        },
        {
          id: 'yachiyoza-1910',
          x: 0.72,
          y: 0.32,
          title: '明治の芝居小屋',
          body: '八千代座は1910年に建てられた芝居小屋で、1988年12月19日に国の重要文化財に指定されました。廻り舞台や奈落、客席が当時の姿をよく残しています。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/102/3574',
          mapQuery: '八千代座',
        },
      ],
    },
    {
      id: 'daikanbo',
      name: '大観峰',
      area: '阿蘇市・秋の夜明け',
      image: '/assets/scenery/kumamoto/scenes/07_daikanbo.webp',
      hotspots: [
        {
          id: 'daikanbo-936m',
          x: 0.46,
          y: 0.36,
          title: '外輪山の上から',
          body: '標高およそ936メートル。阿蘇谷との高低差はおよそ400メートルあり、阿蘇五岳からくじゅう連山まで見渡せます。秋から冬の早朝には雲海に出合えることがあります。',
          source: 'https://kumamoto.guide/spots/detail/211',
          mapQuery: '大観峰',
        },
        {
          id: 'aso-caldera',
          x: 0.72,
          y: 0.46,
          title: '世界最大級とされるカルデラ',
          body: '阿蘇の外輪山は南北およそ25キロ、東西およそ18キロ、周囲およそ128キロ。阿蘇市はこれを世界最大級と表現しています。カルデラの中ではおよそ4万人が暮らしています。',
          source: 'https://www.city.aso.kumamoto.jp/tourism/spot/mt_aso/',
          mapQuery: '大観峰',
        },
      ],
    },
    {
      id: 'tsujunkyo',
      name: '通潤橋',
      area: '山都町・秋の昼',
      image: '/assets/scenery/kumamoto/scenes/08_tsujunkyo.webp',
      hotspots: [
        {
          id: 'tsujun-kokuho',
          x: 0.46,
          y: 0.42,
          title: '2023年に国宝になった',
          body: '嘉永7年、1854年に完成した石造りの水路橋です。1960年に重要文化財、2023年9月25日に国宝に指定されました。近世最大級の石造アーチを渓谷に架け、鞘石垣などの技術で耐震性を高めています。',
          source: 'https://kunishitei.bunka.go.jp/heritage/detail/102/3578',
          mapQuery: '通潤橋',
        },
        {
          id: 'tsujun-siphon',
          x: 0.72,
          y: 0.3,
          title: '水を高いところへ運ぶ',
          body: '白糸台地は水に乏しい土地でした。谷をまたぐために逆サイホンの原理が使われ、いったん低いところを通した水を、向こう岸で再び押し上げる仕組みになっています。',
          source: 'https://www.maff.go.jp/j/nousin/sekkei/museum/m_kakuti/59_tuujyu/index.html',
          mapQuery: '通潤橋',
        },
        {
          id: 'tsujun-hosui',
          x: 0.22,
          y: 0.5,
          title: '土砂を出す放水口',
          body: '橋の上の中央には、土砂を外へ出すための放水口が設けられています。通潤橋は江戸時代に完成したあとも、農業用水路橋として役目を果たしています。',
          source: 'https://www.maff.go.jp/j/nousin/sekkei/museum/m_kakuti/59_tuujyu/index.html',
          mapQuery: '通潤橋',
        },
      ],
    },
    {
      id: 'sakitsu',
      name: '﨑津集落',
      area: '天草市・冬の夕方',
      image: '/assets/scenery/kumamoto/scenes/09_sakitsu.webp',
      solemn: true,
      hotspots: [
        {
          id: 'sakitsu-isan',
          x: 0.46,
          y: 0.44,
          title: '2018年の世界文化遺産',
          body: '「長崎と天草地方の潜伏キリシタン関連遺産」の構成資産として、2018年6月30日に世界文化遺産に登録されました。禁教下の取り締まりとひそかな信仰、解禁後のキリスト教への復活を示す歴史的な景観と評価されています。',
          source: 'https://www.pref.kumamoto.jp/soshiki/22/344.html',
          mapQuery: '﨑津集落',
        },
        {
          id: 'sakitsu-awabi',
          x: 0.24,
          y: 0.36,
          title: 'アワビの貝殻の模様',
          body: '潜伏していた人たちは、身近なものを信心具の代わりにしました。大黒天や恵比須を神に見立て、アワビの貝殻の内側の模様を聖母マリアに見立てたと伝えられています。',
          source: 'https://kirishitan.jp/components/com004',
          mapQuery: '﨑津集落',
        },
        {
          id: 'sakitsu-tatami',
          x: 0.72,
          y: 0.34,
          title: '畳の敷かれた教会',
          body: '﨑津教会は1934年、庄屋役宅の跡地に建てられ、内部は建設当初からの畳敷きです。祭壇は、かつて「絵踏」が行われた場所に置かれたと伝わっています。',
          source: 'https://www.city.amakusa.kumamoto.jp/sakitsu-sekai/kiji0039/index.html',
          mapQuery: '﨑津教会',
        },
      ],
    },
    {
      id: 'kumamotojo',
      name: '熊本城',
      area: '熊本市・冬の夜',
      image: '/assets/scenery/kumamoto/scenes/10_kumamotojo.webp',
      hotspots: [
        {
          id: 'kumamotojo-tenshu',
          x: 0.46,
          y: 0.4,
          title: '2021年に復旧した天守閣',
          body: '天守閣は2021年3月に復旧が終わりました。内部は地上6階地下1階で、最上階からは熊本市内や阿蘇の山並みを見渡せます。いまの天守閣は、西南戦争開戦直前の火災で焼けたあと、1960年に鉄筋鉄骨コンクリートで再建されたものです。',
          source: 'https://castle.kumamoto-guide.jp/about/',
          mapQuery: '熊本城',
        },
        {
          id: 'kumamotojo-2052',
          x: 0.72,
          y: 0.5,
          title: '復旧は2052年度まで',
          body: '城全体の復旧は2052年度までの計画です。特別見学通路の上からは、被害の跡と工事の進み具合を見ることができます。',
          source: 'https://www.city.kumamoto.jp/kiji00348172/5_48172_340982_up_BKB1J4WQ.pdf',
          mapQuery: '熊本城 特別見学通路',
        },
        {
          id: 'kumamotojo-utoyagura',
          x: 0.22,
          y: 0.34,
          title: '解体して復旧中の宇土櫓',
          body: '宇土櫓は、熊本城で築城当初から残る唯一の多重櫓で、国の重要文化財です。2016年の熊本地震で一部が倒壊するなど大きな被害を受け、2022年から解体しての復旧が進められています。',
          source: 'https://castle.kumamoto-guide.jp/grand-unveiling/',
          mapQuery: '熊本城',
        },
      ],
    },
  ],
};
